import {useCallback, useMemo} from 'react';

import useLocalStorageColumnWidthStore from 'notebook/structured-tables/hooks/useLocalStorageColumnWidthStore';
import {StructuredTableColumnWidthStore} from 'notebook/structured-tables/hooks/useStructuredTableColumnWidths';
import {
  DedicatedLookupStructuredTableColumn,
  StructuredTableColumn,
} from 'notebook/structured-tables/StructuredTableConstants';
import {TableColumnType} from 'notebook/tables/constants/TableColumnConstants';

/**
 * A key that identifies the same logical column across reloads. Some tables mint a fresh
 * provenanceUuid per column on every rebuild, so prefer a rebuild-stable id: a schema field id, else
 * a lookup config id. The prefixes keep the id spaces from aliasing.
 *
 * A column with neither (e.g. a built-in ID/Status/Assignee column) has only its column type. That is
 * coarse -- two columns of the same type would share it -- so key by `type:` only when the type is
 * unique among the render's identity-less columns (`typeIsUniqueFallback`), else fall back to the
 * provenanceUuid, which never collides but does not persist across a rebuild.
 */
function stableColumnKey(
  column: StructuredTableColumn,
  typeIsUniqueFallback: (columnType: TableColumnType) => boolean
): string {
  if (column.schemaFieldApiId != null) {
    return `field:${column.schemaFieldApiId}`;
  }
  if (column.tagSchemaFieldId != null) {
    return `field-id:${column.tagSchemaFieldId}`;
  }
  if (column.columnType === TableColumnType.DEDICATED_LOOKUP) {
    return `lookup:${(column as DedicatedLookupStructuredTableColumn).lookupConfig.id}`;
  }
  if (typeIsUniqueFallback(column.columnType)) {
    return `type:${column.columnType}`;
  }
  return `uuid:${column.provenanceUuid}`;
}

/** True for a column with no schema-field id and no lookup id, so its only stable key is its type. */
function hasNoStableIdentity(column: StructuredTableColumn): boolean {
  return (
    column.schemaFieldApiId == null &&
    column.tagSchemaFieldId == null &&
    column.columnType !== TableColumnType.DEDICATED_LOOKUP
  );
}

/**
 * Maps each column's provenanceUuid to its {@link stableColumnKey}, computed over the whole set so
 * `type:` keying can be limited to types that appear exactly once among the identity-less columns.
 */
function stableKeysByProvenanceUuid(columns: ReadonlyArray<StructuredTableColumn>): Record<string, string> {
  const fallbackTypeCounts = new Map<TableColumnType, number>();
  for (const column of columns) {
    if (hasNoStableIdentity(column)) {
      fallbackTypeCounts.set(column.columnType, (fallbackTypeCounts.get(column.columnType) ?? 0) + 1);
    }
  }
  const typeIsUniqueFallback = (columnType: TableColumnType): boolean =>
    fallbackTypeCounts.get(columnType) === 1;
  const keys: Record<string, string> = {};
  for (const column of columns) {
    keys[column.provenanceUuid] = stableColumnKey(column, typeIsUniqueFallback);
  }
  return keys;
}

/**
 * A localStorage-backed {@link StructuredTableColumnWidthStore} that persists widths under each
 * column's stable key (see {@link stableColumnKey}) and translates at the boundary: it overlays stored
 * widths onto the current columns' provenanceUuids on read, and writes each resize back under its
 * column's stable key.
 *
 * Widths for columns not present in this render are left untouched, so two tables that share a storage
 * key but show different columns do not clobber each other's stored widths.
 */
export default function useStructuredTableColumnWidthStoreByStableKey({
  storageKey,
  columns,
}: {
  storageKey: string;
  columns: ReadonlyArray<StructuredTableColumn>;
}): StructuredTableColumnWidthStore {
  const backing = useLocalStorageColumnWidthStore(storageKey);
  const {widthsByColumnId: storedByStableKey, updateWidths} = backing;

  const stableKeyByProvenanceUuid = useMemo(() => stableKeysByProvenanceUuid(columns), [columns]);

  const widthsByColumnId = useMemo(() => {
    const widths: Record<string, number> = {};
    for (const column of columns) {
      const storedWidth = storedByStableKey[stableKeyByProvenanceUuid[column.provenanceUuid]];
      if (storedWidth != null) {
        widths[column.provenanceUuid] = storedWidth;
      }
    }
    return widths;
  }, [columns, storedByStableKey, stableKeyByProvenanceUuid]);

  const setWidthsByColumnId = useCallback(
    (nextWidths: Record<string, number>): void => {
      // Merge onto the value currently in localStorage, not our cached snapshot, so a concurrent
      // instance or tab sharing this storage key does not have its stored widths clobbered. Touch only
      // the keys whose width this instance actually changed: writing every rendered column would push
      // this instance's stale value for the unchanged columns back over a fresher one another instance
      // just wrote. Every other key is carried through untouched.
      updateWidths((currentByStableKey) => {
        const merged = {...currentByStableKey};
        for (const column of columns) {
          const nextWidth = nextWidths[column.provenanceUuid];
          if (nextWidth === widthsByColumnId[column.provenanceUuid]) {
            continue;
          }
          const stableKey = stableKeyByProvenanceUuid[column.provenanceUuid];
          if (nextWidth != null) {
            merged[stableKey] = nextWidth;
          } else {
            delete merged[stableKey];
          }
        }
        return merged;
      });
    },
    [columns, widthsByColumnId, updateWidths, stableKeyByProvenanceUuid]
  );

  return useMemo(() => ({widthsByColumnId, setWidthsByColumnId}), [widthsByColumnId, setWidthsByColumnId]);
}
